/**
 * A key/value store for the viewer's own presentation state: which reading of a collection they
 * prefer, column widths, dismissed notes.
 *
 * Every read is asynchronous so a server-backed implementation fits the same shape as `localStorage`;
 * a store that *can* answer synchronously exposes `readSync` so the first render is not a flash of
 * fallback. Keys are plain names (`orders.grid`); a store decides how to namespace them.
 */
export interface PreferencesStore {
  /** The stored value, or `undefined` when nothing is stored or the stored value is unreadable. */
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
  /**
   * Notifies whenever `key` changes, in this tab or another. The callback receives the new value, or
   * `undefined` once it has been removed.
   */
  subscribe(key: string, callback: (value: unknown) => void): () => void;
  /** Synchronous read for stores that can answer without waiting (memory, `localStorage`). */
  readSync?<T>(key: string): T | undefined;
}

export interface LocalPreferencesStoreOptions {
  /**
   * The prefix every key is stored under, supplied by the app so a preference never leaks across a user
   * boundary on a shared browser: `zagru.<userId>`. A stored key becomes `<namespace>.<key>`.
   */
  namespace: string;
  /** Defaults to `window.localStorage`; injectable for tests and for `sessionStorage`. */
  storage?: Storage;
  /** The window whose `storage` events carry other tabs' writes. Defaults to the global `window`. */
  eventTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

type Listener = (value: unknown) => void;

/** Listener bookkeeping shared by every store here. */
function createListenerMap() {
  const listeners = new Map<string, Set<Listener>>();
  return {
    add(key: string, callback: Listener): () => void {
      let set = listeners.get(key);
      if (!set) {
        set = new Set();
        listeners.set(key, set);
      }
      set.add(callback);
      return () => {
        set?.delete(callback);
        if (set?.size === 0) listeners.delete(key);
      };
    },
    emit(key: string, value: unknown): void {
      listeners.get(key)?.forEach((callback) => callback(value));
    },
  };
}

function parseJson<T>(raw: string | null): T | undefined {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    // A preference that cannot be parsed is a preference nobody set. It must never break the screen.
    return undefined;
  }
}

function defaultStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    // Blocked site data throws on access in some browsers.
    return undefined;
  }
}

/**
 * `localStorage`-backed store: values are JSON under `<namespace>.<key>`, and a write in another tab
 * reaches this one through the `storage` event. A storage that is unavailable (private window, blocked
 * site data) degrades to a store that remembers nothing, never to an exception.
 */
export function createLocalPreferencesStore(options: LocalPreferencesStoreOptions): PreferencesStore {
  const storage = options.storage ?? defaultStorage();
  const prefix = `${options.namespace}.`;
  const listeners = createListenerMap();
  const fullKey = (key: string) => `${prefix}${key}`;

  const readSync = <T,>(key: string): T | undefined => {
    if (!storage) return undefined;
    try {
      return parseJson<T>(storage.getItem(fullKey(key)));
    } catch {
      return undefined;
    }
  };

  const target =
    options.eventTarget ?? (typeof window === 'undefined' ? undefined : (window as Window));
  let detach: (() => void) | undefined;
  let subscriberCount = 0;
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea && storage && event.storageArea !== storage) return;
    if (event.key === null) return;
    if (!event.key.startsWith(prefix)) return;
    listeners.emit(event.key.slice(prefix.length), parseJson(event.newValue));
  };
  const attach = () => {
    if (!target || detach) return;
    target.addEventListener('storage', onStorage);
    detach = () => target.removeEventListener('storage', onStorage);
  };

  return {
    readSync,
    get: async (key) => readSync(key),
    set: async (key, value) => {
      try {
        storage?.setItem(fullKey(key), JSON.stringify(value));
      } catch {
        // Quota exceeded or storage unavailable: the choice lasts for this tab only.
      }
      listeners.emit(key, value);
    },
    remove: async (key) => {
      try {
        storage?.removeItem(fullKey(key));
      } catch {
        // See `set`.
      }
      listeners.emit(key, undefined);
    },
    subscribe: (key, callback) => {
      subscriberCount += 1;
      attach();
      const off = listeners.add(key, callback);
      return () => {
        off();
        subscriberCount -= 1;
        if (subscriberCount === 0 && detach) {
          detach();
          detach = undefined;
        }
      };
    },
  };
}

export interface MemoryPreferencesStore extends PreferencesStore {
  readSync<T>(key: string): T | undefined;
  /** Everything stored, for assertions. */
  snapshot(): Record<string, unknown>;
}

/** In-memory store for tests, Storybook-style labs and a signed-out shell. */
export function createMemoryPreferencesStore(initial: Record<string, unknown> = {}): MemoryPreferencesStore {
  const values = new Map<string, unknown>(Object.entries(initial));
  const listeners = createListenerMap();
  return {
    readSync: <T,>(key: string) => values.get(key) as T | undefined,
    get: async <T,>(key: string) => values.get(key) as T | undefined,
    set: async (key, value) => {
      values.set(key, value);
      listeners.emit(key, value);
    },
    remove: async (key) => {
      values.delete(key);
      listeners.emit(key, undefined);
    },
    subscribe: (key, callback) => listeners.add(key, callback),
    snapshot: () => Object.fromEntries(values),
  };
}

/**
 * Two stores as one: the `mirror` (usually local) answers first and synchronously, the `primary`
 * (usually the server) is the source of truth. Reads that miss the mirror fall through to the primary
 * and backfill the mirror; writes go to both. A preference then survives a new laptop without every
 * first render waiting on the network.
 */
export function createLayeredPreferencesStore(primary: PreferencesStore, mirror: PreferencesStore): PreferencesStore {
  const store: PreferencesStore = {
    get: async <T,>(key: string) => {
      const mirrored = await mirror.get<T>(key);
      if (mirrored !== undefined) return mirrored;
      const value = await primary.get<T>(key);
      if (value !== undefined) await mirror.set(key, value);
      return value;
    },
    set: async (key, value) => {
      await Promise.all([mirror.set(key, value), primary.set(key, value)]);
    },
    remove: async (key) => {
      await Promise.all([mirror.remove(key), primary.remove(key)]);
    },
    subscribe: (key, callback) => {
      const offMirror = mirror.subscribe(key, callback);
      const offPrimary = primary.subscribe(key, callback);
      return () => {
        offMirror();
        offPrimary();
      };
    },
  };
  if (mirror.readSync) {
    const readSync = mirror.readSync.bind(mirror);
    store.readSync = <T,>(key: string) => readSync<T>(key) ?? primary.readSync?.<T>(key);
  } else if (primary.readSync) {
    store.readSync = primary.readSync.bind(primary);
  }
  return store;
}
